import React, { useMemo, useState } from 'react';
import { Flex, useDisclosure } from '@withjoy/joykit';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { AccommodationPlaceType, GuestTravelMapComponentFragment } from '@graphql/generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTravelMapData } from './hooks/useTravelMapData';
import { ExploreOtherOptions } from './components/ExploreOtherOptions';
import { AccommodationsTileV2, AccommodationsTileV2Skeleton } from './components/AccommodationsV2/AccommodationsTileV2';
import { AccommodationsTileV2Favorite, AccommodationsTileV2FavoriteSkeleton } from './components/AccommodationsV2/AccommodationsTileV2Favorite';
import { AccommodationsDialog } from './components/AccommodationsDialog';
import { AccommodationsMobileDialog } from './components/AccommodationsMobileDialog';
import { AccommodationCombined, AccommodationCombinedType } from './components/Accommodations/Accommodation.types';
import { AccommodationsMapV2, AccommodationsMapV2Skeleton } from './components/AccommodationsMapV2';
import { useCustomPageTelemetry } from '../../Custom.telemetry';
import { JoyInteractableMapProvider } from '@shared/components/JoyInteractableMap/JoyInteractableMap.provider';
import { RedirectWithSafeLink } from '@apps/guest/components/RedirectWithSafeLink';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { useEventInfo } from '@shared/utils/eventInfo';
import { isInIframe } from '@shared/utils/isInIframe';
import { useTravelPlacesPricingAndBookingDetailsQuery } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';
import { useTravelPlacesPriceComparisonQuery } from '@shared/hooks/useTravelPlacesPriceComparison';
import { useTravelReservationsSummary } from './hooks/useTravelReservationsSummary';
import { useAccommodationPlacePhotos } from './hooks/useAccommodationPlacePhotos';
import { useReminderModalTrigger } from './hooks/useReminderModalTrigger';
import { ReminderModal } from '@shared/components/ReminderModal';
import { AccommodationsHotelsCount } from './components/AccommodationsHotelsCount';

interface TravelMapProps {
  pageId: string;
  eventId: string;
  pageSlug: string;
  eventHandle: string;
  pageTitle?: string;
  subTitle?: string | null;
  isBrannanLayout?: boolean;
  travelMapComponentData: GuestTravelMapComponentFragment;
}

export const TravelMapV2: React.FC<TravelMapProps> = ({ pageSlug, eventId, eventHandle, pageTitle, isBrannanLayout, travelMapComponentData }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [safeLink, setSafeLink] = useState<string | undefined>();
  const [selectedAccommodationId, setSelectedAccommodationId] = useState<string | null>(null);
  const [selectedAccommodation, setSelectedAccommodation] = useState<AccommodationCombined | undefined>(undefined);
  const [locationBeingHovered, setLocationBeingHovered] = useState<string | undefined>(undefined);
  // typical way to check if the page is in preview mode
  const isPreviewMode = isInIframe();
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const isGridView = isBrannanLayout && !isMobile;

  const isMobileDevice = useIsMobileScreen();
  const { pageColor } = useColorPaletteProvider();
  const customMapStyles = pageColor ? { backgroundColor: 'white', paddingTop: isBrannanLayout ? 10 : 0, paddingBottom: isBrannanLayout ? 10 : 0 } : {};
  const customMapWrapperStyles = pageColor ? { marginTop: 5 } : {};

  const {
    loading,
    showDefaultData,
    destination,
    eventPhoto,
    accommodations: accommodationsData,
    sortedHotelList,
    eventTimezone,
  } = useTravelMapData(eventId, travelMapComponentData);

  const { finalizedEventDate, loading: eventInfoLoading } = useEventInfo();
  const { showReminderModal, handleReminderModalClose } = useReminderModalTrigger(finalizedEventDate);

  const accommodationPlaceIds = accommodationsData.filter(accommodation => accommodation.type !== AccommodationCombinedType.Roomblock).map(accommodation => accommodation.id);
  const joyPlaceIds =
    accommodationsData
      .filter(accommodation => !!accommodation.joyPlaceId && accommodation?.accommodationType === AccommodationPlaceType.hotel)
      .map(accommodation => accommodation.joyPlaceId)
      ?.filter(Boolean) ?? [];

  const favoriteAccommodation = accommodationsData.find(roomBlock => roomBlock.isFavorite);
  const { primaryAccommodation, accommodations } = useMemo(() => {
    const primaryAccommodation = favoriteAccommodation ?? accommodationsData.length > 0 ? accommodationsData[0] : null;
    const accommodations = favoriteAccommodation ?? accommodationsData.length > 0 ? accommodationsData.slice(1) : [];
    return {
      primaryAccommodation,
      accommodations,
    };
  }, [favoriteAccommodation, accommodationsData]);

  const telemetry = useCustomPageTelemetry();

  const { getReservationsCountData } = useTravelReservationsSummary(joyPlaceIds as string[]);
  const { accommodationPropertyPhotos } = useAccommodationPlacePhotos(joyPlaceIds as string[]);

  const onSelectHotel = useEventCallback((id: string, label: 'viewDetail' | 'viewPreview') => {
    setSelectedAccommodationId(id);
    const accommodation = accommodationsData.map((item, index) => ({ ...item, index })).find(accommodation => accommodation.id === id);
    if (accommodation) {
      const telemetryHotelInfo = {
        hotelName: accommodation.displayName || '',
        isHotelPreferred: accommodation.isFavorite || false,
        hotelPrice: accommodation.pricePerNight || undefined,
        hotelStrikeThroughPrice: accommodation.strikeoutPricePerNight || undefined,
        type: accommodation.type,
      };
      telemetry.openAccommodationsDialog('accommodations', telemetryHotelInfo, label);
      setSelectedAccommodation(accommodation);
      onOpen();
    }
  });

  // Show map if have at least one venue or hotel with latitude or longitude
  const shouldShowMap = useMemo(() => accommodationsData?.find(accommodation => accommodation.latitude && accommodation.longitude), [accommodationsData]);

  const renderMap = useMemo(() => {
    return (
      <SkeletonGroup
        isReady={!loading}
        placeholder={<AccommodationsMapV2Skeleton isBrannanLayout={isBrannanLayout} />}
        width="100%"
        paddingX={{ _: 0, md: 6 }}
      >
        {shouldShowMap && (
          <AccommodationsMapV2
            key={`accommodations-guest-map-${destination.latitude}-${destination.longitude}`}
            showFullScreenMap={false}
            venueInfo={destination}
            accommodationsCombined={accommodationsData ?? []}
            pageTitle={pageTitle}
            onSelectHotel={id => onSelectHotel(id, 'viewPreview')}
            isBrannanLayout={isBrannanLayout}
            locationBeingHovered={locationBeingHovered}
            hideDirection
          />
        )}
      </SkeletonGroup>
    );
  }, [isBrannanLayout, loading, locationBeingHovered, onSelectHotel, pageTitle, destination, accommodationsData, shouldShowMap]);

  return (
    <JoyInteractableMapProvider>
      <Flex
        flexDirection="column"
        alignItems="center"
        width="100%"
        rowGap={{ _: 7, sm2: isBrannanLayout ? 7 : 8 }}
        {...customMapWrapperStyles}
      >
        <Flex
          flexDirection="column"
          alignItems="center"
          width="100%"
          rowGap={{ _: 0, sm2: isBrannanLayout ? 7 : 0 }}
          paddingTop={{ _: 8, sm2: isBrannanLayout ? 5 : 8 }}
          {...customMapStyles}
        >
          <Flex
            width="100%"
            paddingX={{ _: 0, sm2: isBrannanLayout ? 7 : 0 }}
          >
            <Flex
              width="100%"
              display={isGridView ? 'grid' : 'flex'}
              flexDirection="column"
              gridTemplateColumns="1fr 2fr"
              gap={{ _: 7, sm2: isBrannanLayout ? 0 : 7 }}
            >
              {shouldShowMap && renderMap}
              <SkeletonGroup
                marginY={{ sm2: isBrannanLayout ? -6 : 0 }}
                isReady={!loading}
                placeholder={<AccommodationsTileV2FavoriteSkeleton />}
              >
                {primaryAccommodation && (
                  <AccommodationsTileV2Favorite
                    accommodation={primaryAccommodation}
                    isFirst={true}
                    isLast={false}
                    locationInfo={destination}
                    eventHandle={eventHandle}
                    reservationCount={getReservationsCountData(primaryAccommodation.joyPlaceId) ?? 0}
                    accommodationPropertyPhotos={
                      accommodationPropertyPhotos?.searchAccommodationProperties?.properties?.find(property => property.joyPlaceId === primaryAccommodation.joyPlaceId)?.photos
                    }
                    showDefaultData={showDefaultData}
                    setLocationBeingHovered={setLocationBeingHovered}
                    from="hotel_map_list"
                  />
                )}
              </SkeletonGroup>
            </Flex>
          </Flex>
          <Flex
            width="100%"
            paddingX={{ _: isBrannanLayout ? 3 : 0, sm2: isBrannanLayout ? 7 : 0 }}
          >
            <Flex
              width="100%"
              flexDirection="column"
              gap={7}
            >
              <SkeletonGroup
                isReady={!loading}
                placeholder={
                  <>
                    <AccommodationsTileV2Skeleton />
                    <AccommodationsTileV2Skeleton />
                    <AccommodationsTileV2Skeleton />
                  </>
                }
                width="100%"
                display={isGridView ? 'grid' : 'flex'}
                flexDirection="column"
                gridTemplateColumns={{
                  _: 'repeat(1, minmax(0px, 1fr))',
                  sm2: 'repeat(2, minmax(0px, 1fr))',
                  md: 'repeat(3, minmax(0px, 1fr))',
                }}
              >
                {accommodations && accommodations?.length > 0 && (
                  <>
                    {accommodations.map((accommodation, idx) => {
                      return (
                        <AccommodationsTileV2
                          accommodation={accommodation}
                          key={`${accommodation.id}-${idx}`}
                          reservationCount={getReservationsCountData(accommodation.joyPlaceId) ?? 0}
                          idx={idx + 1}
                          locationInfo={destination}
                          accommodationPropertyPhotos={
                            accommodationPropertyPhotos?.searchAccommodationProperties?.properties?.find(property => property.joyPlaceId === accommodation.joyPlaceId)?.photos
                          }
                          eventHandle={eventHandle}
                          showDefaultData={showDefaultData}
                          setLocationBeingHovered={setLocationBeingHovered}
                          from="accom_page_grid"
                        />
                      );
                    })}
                  </>
                )}
              </SkeletonGroup>
            </Flex>
          </Flex>
        </Flex>
        {!travelMapComponentData.hideExploreCTA && (
          <ExploreOtherOptions
            accommodations={sortedHotelList}
            venue={destination || null}
            pageSlug={pageSlug}
            eventTimezone={eventTimezone}
          />
        )}
        <RedirectWithSafeLink
          href={safeLink}
          skipTelemetry
          onClose={() => {
            setSafeLink(undefined);
          }}
        />
        {selectedAccommodation &&
          (isMobileDevice ? (
            <AccommodationsMobileDialog
              isOpen={isOpen}
              onClose={onClose}
              eventHandle={eventHandle}
              locationInfo={destination}
              accommodations={accommodationsData}
              selectedAccommodationId={selectedAccommodationId}
              getReservationsCountData={getReservationsCountData}
              accommodationPropertyPhotos={accommodationPropertyPhotos}
            />
          ) : (
            <AccommodationsDialog
              isOpen={isOpen}
              onClose={onClose}
              pageTitle={pageTitle}
              eventPhoto={eventPhoto}
              venueInfo={destination}
              setSafeLink={setSafeLink}
              eventHandle={eventHandle}
              locationInfo={destination}
              shouldShowMap={!!shouldShowMap}
              showDefaultData={showDefaultData}
              accommodation={selectedAccommodation}
            />
          ))}
      </Flex>
      <ReminderModal
        telemetry={telemetry}
        isOpen={showReminderModal}
        onClose={handleReminderModalClose}
        eventId={eventId}
        showCloseButton={false}
      />
      {!eventInfoLoading && !isPreviewMode && !loading && (
        <RequestAccommodationsPricing
          accommodationPlaceIds={accommodationPlaceIds}
          joyPlaceIds={joyPlaceIds as string[]}
          accommodations={accommodationsData}
        />
      )}
    </JoyInteractableMapProvider>
  );
};

const RequestAccommodationsPricing = ({
  accommodationPlaceIds,
  joyPlaceIds,
  accommodations,
}: {
  accommodationPlaceIds: string[];
  joyPlaceIds: string[];
  accommodations: AccommodationCombined[];
}) => {
  const { data: travelPlacesPricingAndBookingDetailsData, loading: travelPlacesPricingAndBookingDetailsLoading } = useTravelPlacesPricingAndBookingDetailsQuery(
    accommodationPlaceIds,
    'accommodationPlace',
  );
  const { data: travelPlacesPriceComparisonData, loading: travelPlacesPriceComparisonLoading } = useTravelPlacesPriceComparisonQuery(joyPlaceIds);

  const isLoading = travelPlacesPricingAndBookingDetailsLoading || travelPlacesPriceComparisonLoading;

  const priceDataList = travelPlacesPricingAndBookingDetailsData?.travelPlacesPricingAndBookingDetails.accommodationPlaces ?? [];
  const travelPlaces = travelPlacesPriceComparisonData?.travelRateComparisonsForTravelPlaces ?? [];

  return isLoading ? null : (
    <AccommodationsHotelsCount
      accommodations={accommodations}
      priceDataList={priceDataList}
      travelPlaces={travelPlaces}
    />
  );
};
